import React from 'react';
import { Stack, TextField } from '@mui/material';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import UnarchiveOutlined from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { Modal, ToolbarButton, type ActionMenuItem } from '../../ui-kit';

/**
 * The ⋯ menu on every tile on the Agents page — a workspace agent or a saved
 * analysis — built in one place so the two can never drift apart: the same
 * words, the same icons, the same order, Delete in red at the bottom.
 * An option a tile cannot offer is simply left out.
 */
export function tileMenu({
  onOpen, onEdit, archived, onArchiveToggle, onDelete,
}: {
  onOpen?: () => void;
  onEdit?: () => void;
  archived?: boolean;
  onArchiveToggle?: () => void;
  onDelete?: () => void;
}): ActionMenuItem[] {
  const items: ActionMenuItem[] = [];
  if (onOpen) items.push({ key: 'open', label: 'Open', icon: <OpenInNewRounded fontSize="small" />, onSelect: onOpen });
  if (onEdit) {
    items.push({ key: 'edit', label: 'Edit name and description', icon: <EditOutlined fontSize="small" />, onSelect: onEdit });
  }
  if (onArchiveToggle) {
    items.push({
      key: 'archive',
      label: archived ? 'Restore' : 'Archive',
      icon: archived ? <UnarchiveOutlined fontSize="small" /> : <Inventory2Outlined fontSize="small" />,
      onSelect: onArchiveToggle,
    });
  }
  if (onDelete) {
    items.push({ key: 'delete', label: 'Delete', tone: 'danger', icon: <DeleteOutlineRounded fontSize="small" />, onSelect: onDelete });
  }
  return items;
}

/** "Edit name and description", the same window for every tile. */
export function EditDetailsModal({
  title, name: initialName, description: initialDescription, onClose, onSave,
}: {
  title: string;
  name: string;
  description: string;
  onClose: () => void;
  onSave: (change: { name: string; description: string }) => Promise<void>;
}) {
  const [name, setName] = React.useState(initialName);
  const [description, setDescription] = React.useState(initialDescription);
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setProblem(null);
    try {
      await onSave({ name: name.trim(), description: description.trim() });
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${title}`}
      subtitle="How it is named and described in this workspace"
      size="sm"
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <ToolbarButton variant="ghost" onClick={onClose}>Cancel</ToolbarButton>
          <ToolbarButton variant="brand" onClick={save} loading={saving} disabled={!name.trim()}>Save</ToolbarButton>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <TextField
          id="tile-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 120 }}
          error={!name.trim()}
          helperText={!name.trim() ? 'It needs a name' : problem || ' '}
          size="small"
          autoFocus
          fullWidth
        />
        <TextField
          id="tile-description"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText="A line on what it does, shown on its tile"
          size="small"
          multiline
          minRows={2}
          fullWidth
        />
      </Stack>
    </Modal>
  );
}
